import { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Stack,
  Button,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ChatBubbleOutline as CommentIcon,
  SwapHoriz as StatusIcon,
  SubdirectoryArrowRight as SubStatusIcon,
  OpenInNew as OpenInNewIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { useApiQuery } from "../../hooks/useApi";
import { getSocket } from "../../utils/socket";
import { useNavigate } from "react-router-dom";

const TYPE_FILTERS = [
  { label: "All Activity", value: "ALL" },
  { label: "Comments", value: "COMMENT" },
  { label: "Status Changes", value: "STATUS_CHANGE" },
  { label: "Sub-status Changes", value: "SUBSTATUS_CHANGE" },
];

function getActivityIcon(type) {
  switch (type) {
    case "COMMENT":
      return <CommentIcon sx={{ fontSize: 18, color: "#2563eb" }} />;
    case "STATUS_CHANGE":
      return <StatusIcon sx={{ fontSize: 18, color: "#16a34a" }} />;
    case "SUBSTATUS_CHANGE":
      return <SubStatusIcon sx={{ fontSize: 18, color: "#d97706" }} />;
    default:
      return <StatusIcon sx={{ fontSize: 18, color: "#64748b" }} />;
  }
}

function getActivityBadgeColor(type) {
  switch (type) {
    case "COMMENT":
      return { bg: "#", border: "#bfdbfe" };
    case "STATUS_CHANGE":
      return { bg: "#", border: "#bbf7d0" };
    case "SUBSTATUS_CHANGE":
      return { bg: "#", border: "#fef3c7" };
    default:
      return { bg: "#", border: "#e2e8f0" };
  }
}

function formatTimeAgo(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffSec = Math.floor((now - date) / 1000);

  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function LiveActivityFeed({ feedType = "admin", onSelectJob }) {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState("ALL");
  const [activities, setActivities] = useState([]); // Ordered [oldest ... newest]
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const [isNearBottom, setIsNearBottom] = useState(true);

  const feedScrollRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  const endpoint = feedType === "user" ? "/users/activity" : "/admin/activity";
  const queryKey = [feedType === "user" ? "user-activity" : "admin-activity", filterType, page];
  const queryUrl = `${endpoint}?page=${page}&limit=20${filterType !== "ALL" ? `&type=${filterType}` : ""}`;

  const { data, isLoading, isFetching, refetch } = useApiQuery(
    queryKey,
    queryUrl,
    { keepPreviousData: true }
  );

  const scrollToBottom = (behavior = "smooth") => {
    const el = feedScrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior });
      setUnreadCount(0);
      setIsNearBottom(true);
    }
  };

  // Handle Scroll events to track proximity to bottom
  const handleScroll = () => {
    const el = feedScrollRef.current;
    if (!el) return;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceToBottom < 80;
    setIsNearBottom(nearBottom);
    if (nearBottom) {
      setUnreadCount(0);
    }
  };

  // Sync API response: backend returns newest first [newest ... oldest]
  useEffect(() => {
    if (data?.activities) {
      const container = feedScrollRef.current;

      if (page === 1) {
        // Reverse so oldest is at index 0, newest at end
        const chronological = [...data.activities].reverse();
        setActivities(chronological);
        isInitialLoadRef.current = true;
      } else {
        // Page > 1 (older items): record current scrollHeight before prepending
        if (container) {
          prevScrollHeightRef.current = container.scrollHeight;
        }
        setActivities((prev) => {
          const existingIds = new Set(prev.map((a) => a._id));
          const olderNewItems = data.activities
            .filter((a) => !existingIds.has(a._id))
            .reverse(); // oldest of the batch comes first
          return [...olderNewItems, ...prev];
        });
      }
      setHasMore(page < data.totalPages);
    }
  }, [data, page]);

  // Adjust scroll position after rendering state changes
  useLayoutEffect(() => {
    const container = feedScrollRef.current;
    if (!container) return;

    if (isInitialLoadRef.current && activities.length > 0) {
      // Scroll to bottom on initial load / filter change
      container.scrollTop = container.scrollHeight;
      isInitialLoadRef.current = false;
    } else if (prevScrollHeightRef.current > 0) {
      // Preserve scroll position after prepending older items
      const heightDiff = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop += heightDiff;
      prevScrollHeightRef.current = 0;
    }
  }, [activities]);

  // Real-time socket updates: append new activities to bottom
  useEffect(() => {
    const socket = getSocket();

    const joinRoom = () => {
      if (feedType === "user") {
        socket.emit("join-user-feed");
      } else {
        socket.emit("join-admin");
      }
    };

    if (socket.connected) {
      joinRoom();
    }
    socket.on("connect", joinRoom);

    const handleNewActivity = (newAct) => {
      if (!newAct || !newAct._id) return;
      if (filterType !== "ALL" && newAct.type !== filterType) return;

      setActivities((prev) => {
        if (prev.some((a) => a._id === newAct._id)) return prev;
        return [...prev, newAct]; // Append newest to bottom!
      });

      const container = feedScrollRef.current;
      if (container) {
        const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceToBottom < 100) {
          setTimeout(() => scrollToBottom("smooth"), 50);
        } else {
          setUnreadCount((c) => c + 1);
        }
      }
    };

    socket.on("new-activity", handleNewActivity);

    return () => {
      socket.off("connect", joinRoom);
      socket.off("new-activity", handleNewActivity);
    };
  }, [filterType, feedType]);

  const handleFilterChange = (val) => {
    setFilterType(val);
    setPage(1);
    setUnreadCount(0);
    isInitialLoadRef.current = true;
  };

  const handleJobClick = (jobCustomId) => {
    if (onSelectJob) {
      onSelectJob(jobCustomId);
    } else {
      navigate(`/search-jobs?search=${jobCustomId}`);
    }
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: "1px solid #f1f5f9",
        boxShadow: "0 1px 3px 0 rgba(15,23,42,0.05)",
        backgroundColor: "#ffffff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Feed Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.1rem" }}>
            Live Job Activity
          </Typography>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#22c55e",
              boxShadow: "0 0 0 2px rgba(34,197,94,0.2)",
            }}
          />
        </Box>

        <Tooltip title="Refresh Feed">
          <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
            <RefreshIcon fontSize="small" sx={{ color: "#64748b" }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filter Tabs */}
      <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: "wrap", gap: 0.5, flexShrink: 0 }}>
        {TYPE_FILTERS.map((tf) => (
          <Chip
            key={tf.value}
            label={tf.label}
            size="small"
            onClick={() => handleFilterChange(tf.value)}
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              borderRadius: "6px",
              ...(filterType === tf.value
                ? {
                  backgroundColor: "#1976d2",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#1976d2" },
                }
                : {
                  backgroundColor: "#f1f5f9",
                  color: "#64748b",
                  border: "1px solid #e2e8f0",
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }),
            }}
          />
        ))}
      </Stack>

      <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

      {/* Internal Scroll Feed Container */}
      <Box
        ref={feedScrollRef}
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          pr: 0.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Load Older Activities Button at TOP */}
        {hasMore && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => p + 1)}
              disabled={isFetching}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#475569",
                borderColor: "#cbd5e1",
              }}
            >
              {isFetching ? "Loading older..." : "↑ Load Older Activities"}
            </Button>
          </Box>
        )}

        {/* Skeleton Loading State */}
        {isLoading && page === 1 ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, p: 1.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton variant="text" width="70%" height={14} />
                </Box>
              </Box>
            ))}
          </Stack>
        ) : activities.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              No activity logged yet
            </Typography>
            <Typography variant="caption">
              Job comments, status changes, and sub-status updates will appear here in real time.
            </Typography>
          </Box>
        ) : (
          /* Render Activities Stream: Oldest at Top -> Newest at Bottom */
          activities.map((item) => {
            const badgeStyle = getActivityBadgeColor(item.type);
            const actorName = item.actorId?.name || "System";
            const jobDisplay = item.jobCustomId || "Job";

            return (
              <Box
                key={item._id}
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  border: `.05px solid #f8fafc`,
                  backgroundColor: badgeStyle.bg,
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 6px -1px rgba(15,23,42,0.06)",
                    borderColor: "#cbd5e1",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      backgroundColor: "#ffffff",
                      border: `1px solid ${badgeStyle.border}`,
                      color: "#334155",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {getActivityIcon(item.type)}
                  </Avatar>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {/* Header line: Actor + Job Chip + Time */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.25 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>
                          {actorName}
                        </Typography>
                        <Chip
                          label={`${jobDisplay} • ${item.clientName || ""}`}
                          size="small"
                          onClick={() => handleJobClick(item.jobCustomId)}
                          icon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                          sx={{
                            height: 19,
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            backgroundColor: "#ffffff",
                            border: "1px solid #cbd5e1",
                            color: "#1e293b",
                            cursor: "pointer",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.7rem" }}>
                        {formatTimeAgo(item.createdAt)}
                      </Typography>
                    </Box>

                    {/* Content detail line */}
                    {item.type === "COMMENT" && (
                      <Typography variant="body2" sx={{ color: "#334155", fontStyle: "italic", mt: 0.25, fontSize: "0.82rem" }}>
                        “{item.message}”
                      </Typography>
                    )}

                    {item.type === "STATUS_CHANGE" && (
                      <Box sx={{ mt: 0.25 }}>
                        <Typography variant="body2" sx={{ color: "#166534", fontWeight: 600, fontSize: "0.82rem" }}>
                          Status changed:{" "}
                          <span style={{ textDecoration: "line-through", color: "#94a3b8", fontWeight: 400 }}>
                            {item.metadata?.previousStatus || "new"}
                          </span>{" "}
                          → <strong>{item.metadata?.newStatus}</strong>
                        </Typography>
                        {item.metadata?.reason && (
                          <Typography variant="caption" sx={{ color: "#64748b", display: "block", fontSize: "0.72rem" }}>
                            Reason: {item.metadata.reason}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {item.type === "SUBSTATUS_CHANGE" && (
                      <Box sx={{ mt: 0.25 }}>
                        <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 600, fontSize: "0.82rem" }}>
                          Sub-status changed:{" "}
                          <span style={{ textDecoration: "line-through", color: "#94a3b8", fontWeight: 400 }}>
                            {item.metadata?.previousSubStatus || "none"}
                          </span>{" "}
                          → <strong>{item.metadata?.newSubStatus}</strong>
                        </Typography>
                        {item.metadata?.reason && (
                          <Typography variant="caption" sx={{ color: "#64748b", display: "block", fontSize: "0.72rem" }}>
                            Reason: {item.metadata.reason}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Floating Bottom Unread / Scroll to Bottom Indicator Pill */}
      {(!isNearBottom || unreadCount > 0) && (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
          }}
        >
          <Button
            size="small"
            variant="contained"
            onClick={() => scrollToBottom("smooth")}
            startIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
            sx={{
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: 700,
              backgroundColor: "#2563eb",
              color: "#ffffff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
              textTransform: "none",
              px: 2,
              py: 0.5,
              "&:hover": {
                backgroundColor: "#1d4ed8",
              },
            }}
          >
            {unreadCount > 0 ? `↓ ${unreadCount} new ${unreadCount === 1 ? "activity" : "activities"}` : " Scroll to newest"}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
